import { Link } from "react-router-dom";
import { Compass, Handshake, ArrowRight, CheckCircle2, Lock, TrendingUp } from "lucide-react";
import V2Layout from "@/pages/v2/V2Layout";
import Seo from "@/components/Seo";

export default function V2Approach() {
  return (
    <V2Layout>
      <div data-testid="v2-approach">
        <Seo title="Our Approach · WHO" path="/v2/approach" description="WHO's two pathways: public resource Discovery today, and a planned permission-based Coordinated Services approach supported by WHO Guides." />
        <section className="py-16 lg:py-24 bg-white border-b border-borderSubtle">
          <div className="max-w-[900px] mx-auto px-5 lg:px-8">
            <p className="eyebrow text-gold-deep">Our approach</p>
            <h1 className="mt-3 font-display font-black text-navy tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight" data-testid="v2-approach-title">
              Simple to explore. Careful when it counts.
            </h1>
            <p className="mt-5 text-slateSoft leading-relaxed max-w-2xl" data-testid="v2-approach-lede">
              Discover resources today. Learn about our vision for more coordinated, accountable
              connections tomorrow.
            </p>

            <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid="v2-approach-pathways">
              <div className="p-7 bg-cream-panel border border-borderSubtle rounded-lg">
                <Compass className="w-7 h-7 text-gold-deep" />
                <h2 className="mt-4 font-display font-bold text-navy text-xl">Discover Resources — available now</h2>
                <p className="mt-2 text-sm text-slateSoft leading-relaxed">
                  A public, informational directory of veteran-serving resources — including Veteran
                  Entrepreneurship &amp; Business Ownership. Explore freely and contact organizations directly.
                </p>
              </div>
              <div className="p-7 bg-navy-deep text-cream rounded-lg">
                <Handshake className="w-7 h-7 text-gold" />
                <h2 className="mt-4 font-display font-bold text-cream text-xl">Coordinated Services — planned</h2>
                <p className="mt-2 text-sm text-cream/75 leading-relaxed">
                  A future, permission-based approach for needs that cross several services at once:
                  WHO Guides&trade; would help identify resources, support warm connections, and follow
                  through when a handoff doesn&rsquo;t succeed.
                </p>
              </div>
            </div>

            <div className="mt-10 space-y-4 max-w-2xl" data-testid="v2-approach-principles">
              {[
                { icon: <Lock className="w-4 h-4" />, t: "Permission first — nothing is shared without consent." },
                { icon: <CheckCircle2 className="w-4 h-4" />, t: "Confirmed connections — a referral is not a result." },
                { icon: <TrendingUp className="w-4 h-4" />, t: "Follow-through — unresolved needs stay visible." },
              ].map((p) => (
                <div key={p.t} className="flex items-start gap-3 text-sm text-slateSoft">
                  <span className="mt-0.5 w-7 h-7 rounded-full bg-gold/15 text-gold-deep flex items-center justify-center shrink-0">{p.icon}</span>
                  <p className="leading-relaxed">{p.t}</p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-xs text-slateSoft" data-testid="v2-approach-note">
              Coordinated Services and WHO Guides&trade; are planned capabilities under development — not live
              operational programs. Deeper material:{" "}
              <Link to="/platform" className="text-navy underline decoration-gold underline-offset-2 hover:text-gold-deep">Platform</Link>{" · "}
              <Link to="/impact" className="text-navy underline decoration-gold underline-offset-2 hover:text-gold-deep">Impact &amp; Economic Intelligence</Link>.
            </p>

            <Link to="/v2/resources" className="mt-8 inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream font-semibold px-6 py-3 rounded-md transition-colors" data-testid="v2-approach-cta">
              Explore Resources <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </V2Layout>
  );
}
